// ─────────────────────────────────────────────────────────────────
// Glass-i — Purchase Request + Purchase Order helpers
// (Stage 6P.1 + 6P.2).
//
// Pure ESM module — tested by the frontend node:test runner.
//
// Frontend mirrors of backend money math + payload shaping. The
// backend (`backend/src/services/erp/purchase/`) remains the source
// of truth for sqmt, sqft, total_length, gross, discount, taxable,
// CGST/SGST/IGST, line_total, and header subtotal/discount/tax/
// round_off/grand_total. These helpers exist only to give the grid
// instant feedback while the operator types; the API recomputes on
// every save.
//
// Mirrors the style of:
//   - frontend/src/pages/erp/masters/glassCategories/
//       glassCategoryHelpers.js  (option lists, normalize/build,
//       validation shape)
//   - backend/src/services/erp/purchase/purchaseCalculation.service.js
//       (tolerant numeric coercion, round2/round4, line/header
//       math, snake_case ⇄ camelCase access).
// ─────────────────────────────────────────────────────────────────


// ─────────────────────────────────────────────────────────────────
// Option lists — kept aligned with backend
// services/erp/purchase/purchase.constants.js. Frozen so callers
// can't mutate the shared dropdowns by accident.
// ─────────────────────────────────────────────────────────────────

export const ITEM_TYPE_OPTIONS = Object.freeze([
  { value: 'GLASS',     label: 'Glass'      },
  { value: 'HARDWARE',  label: 'Hardware'   },
  { value: 'ACCESSORY', label: 'Accessory'  },
  { value: 'PROFILE',   label: 'Profile'    },
  { value: 'UPVC',      label: 'uPVC'       },
  { value: 'ALUMINIUM', label: 'Aluminium'  },
  { value: 'SERVICE',   label: 'Service'    },
  { value: 'JOBWORK',   label: 'Job Work'   },
  { value: 'OTHER',     label: 'Other'      }
]);

export const PR_STATUS_OPTIONS = Object.freeze([
  { value: 'DRAFT',            label: 'Draft'            },
  { value: 'REQUESTED',        label: 'Requested'        },
  { value: 'APPROVED',         label: 'Approved'         },
  { value: 'REJECTED',         label: 'Rejected'         },
  { value: 'CONVERTED_TO_PO',  label: 'Converted to PO'  },
  { value: 'CANCELLED',        label: 'Cancelled'        },
  { value: 'CLOSED',           label: 'Closed'           }
]);

export const PO_STATUS_OPTIONS = Object.freeze([
  { value: 'DRAFT',              label: 'Draft'              },
  { value: 'PLACED',             label: 'Placed'             },
  { value: 'PARTIALLY_RECEIVED', label: 'Partially Received' },
  { value: 'CLOSED',             label: 'Closed'             },
  { value: 'CANCELLED',          label: 'Cancelled'          }
]);

export const PR_PRIORITY_OPTIONS = Object.freeze([
  { value: 'LOW',    label: 'Low'    },
  { value: 'NORMAL', label: 'Normal' },
  { value: 'HIGH',   label: 'High'   },
  { value: 'URGENT', label: 'Urgent' }
]);

export const PR_REQUEST_TYPE_OPTIONS = Object.freeze([
  { value: 'GENERAL',             label: 'General'             },
  { value: 'GLASS',               label: 'Glass'               },
  { value: 'HARDWARE',            label: 'Hardware'            },
  { value: 'PROFILE',             label: 'Profile'             },
  { value: 'SERVICE',             label: 'Service'             },
  { value: 'MAINTENANCE',         label: 'Maintenance'         },
  { value: 'PROJECT',             label: 'Project'             },
  { value: 'STOCK_REPLENISHMENT', label: 'Stock Replenishment' }
]);

// Length-based UOMs — see backend purchase.constants.LENGTH_BASED_UOMS.
export const LENGTH_BASED_UOMS = Object.freeze(['RFT', 'RMT', 'MTR']);

// Glass area UOMs — drive the sqmt ↔ sqft conversion path.
export const GLASS_AREA_UOMS = Object.freeze(['SQMT', 'SQFT']);

// 1 SQMT = 10.764 SQFT — matches backend SQMT_TO_SQFT.
export const SQMT_TO_SQFT = 10.764;


// ─────────────────────────────────────────────────────────────────
// Private constants used by the math helpers
// ─────────────────────────────────────────────────────────────────

const ITEM_TYPE_GLASS     = 'GLASS';
const ITEM_TYPE_PROFILE   = 'PROFILE';
const ITEM_TYPE_UPVC      = 'UPVC';
const ITEM_TYPE_ALUMINIUM = 'ALUMINIUM';

const LENGTH_ITEM_TYPES = new Set([
  ITEM_TYPE_PROFILE, ITEM_TYPE_UPVC, ITEM_TYPE_ALUMINIUM
]);

const UOM_SQMT = 'SQMT';
const UOM_SQFT = 'SQFT';

const LENGTH_UOMS_SET = new Set(LENGTH_BASED_UOMS);

const VALID_PR_STATUSES = new Set(PR_STATUS_OPTIONS.map((o) => o.value));
const VALID_PO_STATUSES = new Set(PO_STATUS_OPTIONS.map((o) => o.value));
const VALID_ITEM_TYPES  = new Set(ITEM_TYPE_OPTIONS.map((o) => o.value));


// ─────────────────────────────────────────────────────────────────
// Tolerant numeric coercion — mirrors backend toNumber.
// Null / undefined / empty string / non-finite all collapse to 0.
// Never throws, never returns NaN.
// ─────────────────────────────────────────────────────────────────

function toNumber(value) {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return 0;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : 0;
  }
  if (typeof value === 'boolean') return value ? 1 : 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

// Half-up rounding — defeats IEEE-754 surprises like 1.005 → 1.0049…
// so 1.005 rounds to 1.01, not 1.00, regardless of sign.
function roundHalfUp(value, decimals) {
  const n = toNumber(value);
  if (n === 0) return 0;
  const factor = Math.pow(10, decimals);
  const sign   = n < 0 ? -1 : 1;
  const abs    = Math.abs(n);
  const scaled = abs * factor + 1e-9;
  return (sign * Math.round(scaled)) / factor;
}

export function round2(n) { return roundHalfUp(n, 2); }
export function round4(n) { return roundHalfUp(n, 4); }


// ─────────────────────────────────────────────────────────────────
// Snake / camel access — lines arrive from both shapes (grid edits,
// API responses, tests). We probe both and never mutate the caller.
// ─────────────────────────────────────────────────────────────────

function pick(obj, snake, camel, fallback) {
  if (!obj || typeof obj !== 'object') return fallback;
  if (snake in obj && obj[snake] !== undefined) return obj[snake];
  if (camel in obj && obj[camel] !== undefined) return obj[camel];
  return fallback;
}

function pickStringUpper(obj, snake, camel) {
  const v = pick(obj, snake, camel, '');
  if (v === null || v === undefined) return '';
  return String(v).trim().toUpperCase();
}


// ─────────────────────────────────────────────────────────────────
// Measurement helpers — exported for the editor to drive instant
// recalculation on width / height / qty / length_value edits.
// ─────────────────────────────────────────────────────────────────

// widthMm × heightMm → sqmt, then × qty so multi-piece lines
// aggregate naturally. mm² → m² divides by 1,000,000.
export function calculateSqmt(widthMm, heightMm, qty) {
  const w = toNumber(widthMm);
  const h = toNumber(heightMm);
  const q = toNumber(qty);
  if (w <= 0 || h <= 0 || q <= 0) return 0;
  return round4((w * h * q) / 1000000);
}

export function calculateSqft(sqmt) {
  const s = toNumber(sqmt);
  if (s <= 0) return 0;
  return round4(s * SQMT_TO_SQFT);
}

// length_value × qty for length-driven items. The UOM choice is
// validated upstream — this helper just multiplies.
export function calculateTotalLength(lengthValue, qty) {
  const l = toNumber(lengthValue);
  const q = toNumber(qty);
  if (l <= 0 || q <= 0) return 0;
  return round4(l * q);
}


// ─────────────────────────────────────────────────────────────────
// Line normalisation — mirrors backend
// normalizePurchaseLineMeasurements. Recomputes sqmt / sqft /
// total_length per item_type and zeroes out irrelevant fields so
// stale grid state can't leak into header totals. Returns a NEW
// object carrying both casings.
// ─────────────────────────────────────────────────────────────────

export function normalizePurchaseLineByItemType(line) {
  const safe     = line && typeof line === 'object' ? line : {};
  const itemType = pickStringUpper(safe, 'item_type', 'itemType');
  const qty      = toNumber(pick(safe, 'qty', 'qty', 0));

  let widthMm    = toNumber(pick(safe, 'width_mm',     'widthMm',    0));
  let heightMm   = toNumber(pick(safe, 'height_mm',    'heightMm',   0));
  let lengthVal  = toNumber(pick(safe, 'length_value', 'lengthValue', 0));

  let sqmt        = 0;
  let sqft        = 0;
  let totalLength = 0;

  if (itemType === ITEM_TYPE_GLASS) {
    sqmt        = calculateSqmt(widthMm, heightMm, qty);
    sqft        = calculateSqft(sqmt);
    totalLength = 0;
  } else if (LENGTH_ITEM_TYPES.has(itemType)) {
    totalLength = calculateTotalLength(lengthVal, qty);
    sqmt        = 0;
    sqft        = 0;
    // Glass dims aren't meaningful on length items — clear them.
    widthMm  = 0;
    heightMm = 0;
  } else {
    sqmt        = 0;
    sqft        = 0;
    totalLength = 0;
    widthMm    = 0;
    heightMm   = 0;
    lengthVal  = 0;
  }

  return Object.assign({}, safe, {
    item_type:    itemType || null,
    itemType:     itemType || null,
    qty,
    width_mm:     widthMm,
    widthMm,
    height_mm:    heightMm,
    heightMm,
    length_value: lengthVal,
    lengthValue:  lengthVal,
    sqmt,
    sqft,
    total_length: totalLength,
    totalLength
  });
}


// ─────────────────────────────────────────────────────────────────
// PR line amount — gross only, no tax/discount split. Mirrors
// backend calculatePurchaseRequestLineAmount.
//
//   GLASS + SQMT  → sqmt × estimated_rate
//   GLASS + SQFT  → sqft × estimated_rate
//   GLASS + other → qty  × estimated_rate
//   PROFILE/UPVC/ALUMINIUM + RFT/RMT/MTR → total_length × estimated_rate
//   PROFILE/UPVC/ALUMINIUM + other       → qty × estimated_rate
//   anything else                        → qty × estimated_rate
//
// Caller may pass either snake or camel; the rate column on a PR
// line is `estimated_rate` (falls back to `rate` for symmetry with
// the PO helper if a caller wires them up the same way).
// ─────────────────────────────────────────────────────────────────

export function calculatePurchaseRequestLineAmount(line) {
  const safe     = line && typeof line === 'object' ? line : {};
  const norm     = normalizePurchaseLineByItemType(safe);
  const itemType = pickStringUpper(norm, 'item_type', 'itemType');
  const uom      = pickStringUpper(safe, 'uom', 'uom');
  const rate     =
    toNumber(pick(safe, 'estimated_rate', 'estimatedRate', undefined)) ||
    toNumber(pick(safe, 'rate',           'rate',           0));
  const qty      = toNumber(pick(norm, 'qty', 'qty', 0));

  let basis = 0;

  if (itemType === ITEM_TYPE_GLASS) {
    if (uom === UOM_SQMT)      basis = toNumber(norm.sqmt);
    else if (uom === UOM_SQFT) basis = toNumber(norm.sqft);
    else                       basis = qty;
  } else if (LENGTH_ITEM_TYPES.has(itemType)) {
    if (LENGTH_UOMS_SET.has(uom)) basis = toNumber(norm.total_length);
    else                          basis = qty;
  } else {
    basis = qty;
  }

  return round2(basis * rate);
}


// ─────────────────────────────────────────────────────────────────
// PO line amount with full tax split — mirrors backend
// calculatePurchaseOrderLineAmount. Returns a NEW object carrying
// both casings of every derived field, ready to spread into a grid
// row.
//
// Tax split rule (same as accounts/expenses):
//   - supplierStateCode === companyStateCode (both present)
//       → CGST = SGST = round2(gst_amount / 2); IGST = 0
//   - different OR either missing
//       → IGST = gst_amount; CGST = SGST = 0
// ─────────────────────────────────────────────────────────────────

export function calculatePurchaseOrderLineAmount(line, ctx) {
  const safe     = line && typeof line === 'object' ? line : {};
  const context  = ctx  && typeof ctx  === 'object' ? ctx  : {};
  const supplierState = pickStringUpper(context, 'supplier_state_code', 'supplierStateCode');
  const companyState  = pickStringUpper(context, 'company_state_code',  'companyStateCode');

  const norm = normalizePurchaseLineByItemType(safe);

  // Gross uses the same basis rules as PR — but reads `rate`
  // first (PO uses `rate`, PR uses `estimated_rate`).
  const itemType = pickStringUpper(norm, 'item_type', 'itemType');
  const uom      = pickStringUpper(safe, 'uom', 'uom');
  const rate     = toNumber(pick(safe, 'rate', 'rate', 0));
  const qty      = toNumber(pick(norm, 'qty', 'qty', 0));

  let basis = 0;
  if (itemType === ITEM_TYPE_GLASS) {
    if (uom === UOM_SQMT)      basis = toNumber(norm.sqmt);
    else if (uom === UOM_SQFT) basis = toNumber(norm.sqft);
    else                       basis = qty;
  } else if (LENGTH_ITEM_TYPES.has(itemType)) {
    if (LENGTH_UOMS_SET.has(uom)) basis = toNumber(norm.total_length);
    else                          basis = qty;
  } else {
    basis = qty;
  }

  const grossAmount    = round2(basis * rate);
  const discountPct    = toNumber(pick(safe, 'discount_percent', 'discountPercent', 0));
  const gstRate        = toNumber(pick(safe, 'gst_rate',         'gstRate',         0));

  const discountAmount = round2(grossAmount * discountPct / 100);
  const taxableAmount  = round2(grossAmount - discountAmount);
  const gstAmount      = round2(taxableAmount * gstRate / 100);

  let cgstAmount = 0;
  let sgstAmount = 0;
  let igstAmount = 0;

  const sameState =
    supplierState && companyState && supplierState === companyState;

  if (sameState) {
    const half = round2(gstAmount / 2);
    cgstAmount = half;
    sgstAmount = round2(gstAmount - half); // preserves penny-rounding parity
  } else {
    igstAmount = gstAmount;
  }

  const lineTotal = round2(taxableAmount + cgstAmount + sgstAmount + igstAmount);

  return Object.assign({}, norm, {
    rate,
    uom:              uom || null,

    discount_percent: discountPct,
    discountPercent:  discountPct,
    gst_rate:         gstRate,
    gstRate:          gstRate,

    gross_amount:     grossAmount,
    grossAmount,
    discount_amount:  discountAmount,
    discountAmount,
    taxable_amount:   taxableAmount,
    taxableAmount,
    cgst_amount:      cgstAmount,
    cgstAmount,
    sgst_amount:      sgstAmount,
    sgstAmount,
    igst_amount:      igstAmount,
    igstAmount,
    line_total:       lineTotal,
    lineTotal
  });
}


// ─────────────────────────────────────────────────────────────────
// PO header totals — sums pre-rounded line numbers so the header
// matches the printed line columns to the paisa. roundOff is a
// signed operator adjustment applied to grand_total only.
// ─────────────────────────────────────────────────────────────────

export function calculatePoTotals(lines, options) {
  const opts          = options && typeof options === 'object' ? options : {};
  const list          = Array.isArray(lines) ? lines : [];
  const roundOff      = toNumber(pick(opts, 'round_off', 'roundOff', 0));
  const supplierState = pickStringUpper(opts, 'supplier_state_code', 'supplierStateCode');
  const companyState  = pickStringUpper(opts, 'company_state_code',  'companyStateCode');

  let subtotal       = 0;
  let discountAmount = 0;
  let taxableAmount  = 0;
  let cgstAmount     = 0;
  let sgstAmount     = 0;
  let igstAmount     = 0;

  for (const ln of list) {
    if (!ln || typeof ln !== 'object') continue;
    subtotal       += toNumber(pick(ln, 'gross_amount',    'grossAmount',    0));
    discountAmount += toNumber(pick(ln, 'discount_amount', 'discountAmount', 0));
    taxableAmount  += toNumber(pick(ln, 'taxable_amount',  'taxableAmount',  0));
    cgstAmount     += toNumber(pick(ln, 'cgst_amount',     'cgstAmount',     0));
    sgstAmount     += toNumber(pick(ln, 'sgst_amount',     'sgstAmount',     0));
    igstAmount     += toNumber(pick(ln, 'igst_amount',     'igstAmount',     0));
  }

  subtotal       = round2(subtotal);
  discountAmount = round2(discountAmount);
  taxableAmount  = round2(taxableAmount);
  cgstAmount     = round2(cgstAmount);
  sgstAmount     = round2(sgstAmount);
  igstAmount     = round2(igstAmount);

  // Ad-hoc manual Other Charges (post-tax, no GST) — mirrors the backend
  // calc: amount = (area > 0 ? area : 1) × rate, summed into grand total.
  const rawCharges = pick(opts, 'other_charges', 'otherCharges', null);
  const chargeList = Array.isArray(rawCharges) ? rawCharges : [];
  let otherChargesAmount = 0;
  for (const c of chargeList) {
    if (!c || typeof c !== 'object') continue;
    const name = String(pick(c, 'charge_name', 'chargeName', '') || '').trim();
    if (!name) continue;
    const area = toNumber(pick(c, 'area', 'area', 0));
    const rate = toNumber(pick(c, 'rate', 'rate', 0));
    otherChargesAmount += round2((area > 0 ? area : 1) * rate);
  }
  otherChargesAmount = round2(otherChargesAmount);

  const totalTax   = round2(cgstAmount + sgstAmount + igstAmount);
  const roundOffR  = round2(roundOff);
  const grandTotal = round2(taxableAmount + totalTax + otherChargesAmount + roundOffR);

  return {
    subtotal,
    discount_amount:     discountAmount,
    discountAmount,
    taxable_amount:      taxableAmount,
    taxableAmount,
    cgst_amount:         cgstAmount,
    cgstAmount,
    sgst_amount:         sgstAmount,
    sgstAmount,
    igst_amount:         igstAmount,
    igstAmount,
    total_tax:           totalTax,
    totalTax,
    other_charges_amount: otherChargesAmount,
    otherChargesAmount,
    round_off:           roundOffR,
    roundOff:            roundOffR,
    grand_total:         grandTotal,
    grandTotal,
    supplier_state_code: supplierState || null,
    supplierStateCode:   supplierState || null,
    company_state_code:  companyState  || null,
    companyStateCode:    companyState  || null
  };
}


// ─────────────────────────────────────────────────────────────────
// Stable client row ids — the grid needs a key for brand-new lines
// before the server has assigned a UUID. Mirrors the gc-c-N pattern
// used by glassCategoryHelpers.
// ─────────────────────────────────────────────────────────────────

let _rowIdCounter = 0;
function nextRowId(prefix) {
  _rowIdCounter += 1;
  return `${prefix}-c-${_rowIdCounter}`;
}

function normalizeLine(line, prefix) {
  const safe = line && typeof line === 'object' ? line : {};
  // Reuse existing _clientRowId if the editor already tagged the row;
  // otherwise mint a deterministic id from the server id when known.
  const cid =
    safe._clientRowId ||
    (safe.id ? `${prefix}-${safe.id}` : nextRowId(prefix));
  return Object.assign({}, safe, {
    _clientRowId: cid,
    _dirty:       false
  });
}

// API → editor shape for ad-hoc Other Charges rows. Each gets a stable
// client row id for the editor table.
function normalizeOtherCharges(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((c) => {
      const safe = c && typeof c === 'object' ? c : {};
      const name = safe.chargeName || safe.charge_name || '';
      if (!String(name).trim()) return null;
      return {
        _clientRowId:   nextRowId('poc'),
        manualChargeId: safe.manualChargeId || safe.manual_charge_id || null,
        chargeName:     name,
        area:           toNumber(pick(safe, 'area', 'area', 0)),
        rate:           toNumber(pick(safe, 'rate', 'rate', 0)),
        amount:         toNumber(pick(safe, 'amount', 'amount', 0))
      };
    })
    .filter((c) => c !== null);
}


// ─────────────────────────────────────────────────────────────────
// API → editor shape — purchase request
// ─────────────────────────────────────────────────────────────────

export function normalizePurchaseRequest(api) {
  const safe = api && typeof api === 'object' ? api : {};
  const lines = Array.isArray(safe.lines) ? safe.lines : [];
  return {
    id:                  safe.id                 || null,
    prNo:                safe.prNo               || safe.pr_no               || null,
    prDate:              safe.prDate             || safe.pr_date             || null,
    requiredByDate:      safe.requiredByDate     || safe.required_by_date    || null,
    requestType:         safe.requestType        || safe.request_type        || 'GENERAL',
    priority:            safe.priority           || 'NORMAL',
    status:              safe.status             || 'DRAFT',
    department:          safe.department         || safe.departmentId        || null,
    requestedBy:         safe.requestedBy        || safe.requested_by        || null,
    projectRef:          safe.projectRef         || safe.project_ref         || null,
    customerRef:         safe.customerRef        || safe.customer_ref        || null,
    remarks:             safe.remarks            || null,
    approvedBy:          safe.approvedBy         || safe.approved_by         || null,
    approvedAt:          safe.approvedAt         || safe.approved_at         || null,
    rejectedBy:          safe.rejectedBy         || safe.rejected_by         || null,
    rejectedAt:          safe.rejectedAt         || safe.rejected_at         || null,
    rejectionReason:     safe.rejectionReason    || safe.rejection_reason    || null,
    cancelledBy:         safe.cancelledBy        || safe.cancelled_by        || null,
    cancelledAt:         safe.cancelledAt        || safe.cancelled_at        || null,
    cancellationReason:  safe.cancellationReason || safe.cancellation_reason || null,
    convertedPoId:       safe.convertedPoId      || safe.converted_po_id     || null,
    convertedAt:         safe.convertedAt        || safe.converted_at        || null,
    createdAt:           safe.createdAt          || safe.created_at          || null,
    updatedAt:           safe.updatedAt          || safe.updated_at          || null,
    lines: lines.map((ln) => normalizeLine(ln, 'pr'))
  };
}


// ─────────────────────────────────────────────────────────────────
// API → editor shape — purchase order
// ─────────────────────────────────────────────────────────────────

export function normalizePurchaseOrder(api) {
  const safe = api && typeof api === 'object' ? api : {};
  const lines = Array.isArray(safe.lines) ? safe.lines : [];
  return {
    id:                   safe.id                  || null,
    poNo:                 safe.poNo                || safe.po_no                  || null,
    poDate:               safe.poDate              || safe.po_date                || null,
    expectedDeliveryDate: safe.expectedDeliveryDate|| safe.expected_delivery_date || null,
    status:               safe.status              || 'DRAFT',
    supplierId:           safe.supplierId          || safe.supplier_id            || null,
    supplierName:         safe.supplierName        || safe.supplier_name          || null,
    supplierGstin:        safe.supplierGstin       || safe.supplier_gstin         || null,
    supplierContact:      safe.supplierContact     || safe.supplier_contact       || null,
    supplierAddress:      safe.supplierAddress     || safe.supplier_address       || null,
    supplierStateCode:    safe.supplierStateCode   || safe.supplier_state_code    || null,
    companyStateCode:     safe.companyStateCode    || safe.company_state_code     || null,
    sourcePrId:           safe.sourcePrId          || safe.source_pr_id           || safe.prId || null,
    prNumberSnapshot:     safe.prNumberSnapshot    || safe.pr_number_snapshot     || null,
    deliveryLocation:     safe.deliveryLocation    || safe.delivery_location      || null,
    paymentTerms:         safe.paymentTerms        || safe.payment_terms          || null,
    deliveryTerms:        safe.deliveryTerms       || safe.delivery_terms         || null,
    freightTerms:         safe.freightTerms        || safe.freight_terms          || null,
    remarks:              safe.remarks             || null,

    subtotal:        toNumber(safe.subtotal),
    discountAmount:  toNumber(pick(safe, 'discount_amount', 'discountAmount', 0)),
    taxableAmount:   toNumber(pick(safe, 'taxable_amount',  'taxableAmount',  0)),
    cgstAmount:      toNumber(pick(safe, 'cgst_amount',     'cgstAmount',     0)),
    sgstAmount:      toNumber(pick(safe, 'sgst_amount',     'sgstAmount',     0)),
    igstAmount:      toNumber(pick(safe, 'igst_amount',     'igstAmount',     0)),
    totalTax:        toNumber(pick(safe, 'total_tax',       'totalTax',       0)),
    otherChargesAmount: toNumber(pick(safe, 'other_charges_amount', 'otherChargesAmount', 0)),
    otherCharges:    normalizeOtherCharges(pick(safe, 'other_charges', 'otherCharges', null)),
    shipTo:          normalizeShipTo(pick(safe, 'ship_to', 'shipTo', null)),
    billFrom:        normalizeBillFrom(safe),
    roundOff:        toNumber(pick(safe, 'round_off',       'roundOff',       0)),
    grandTotal:      toNumber(pick(safe, 'grand_total',     'grandTotal',     0)),

    placedBy:            safe.placedBy            || safe.placed_by            || null,
    placedAt:            safe.placedAt            || safe.placed_at            || null,
    cancelledBy:         safe.cancelledBy         || safe.cancelled_by         || null,
    cancelledAt:         safe.cancelledAt         || safe.cancelled_at         || null,
    cancellationReason:  safe.cancellationReason  || safe.cancellation_reason  || null,
    closedBy:            safe.closedBy            || safe.closed_by            || null,
    closedAt:            safe.closedAt            || safe.closed_at            || null,
    createdAt:           safe.createdAt           || safe.created_at           || null,
    updatedAt:           safe.updatedAt           || safe.updated_at           || null,

    lines: lines.map((ln) => normalizeLine(ln, 'po'))
  };
}


// ─────────────────────────────────────────────────────────────────
// Editor → payload builders. Strip client-only metadata
// (_clientRowId, _dirty), emit snake_case to mirror what the Joi
// validators expect, and omit nullish / empty optional fields so
// stripUnknown:true doesn't have to do extra work.
// ─────────────────────────────────────────────────────────────────

function trimOrNull(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s ? s : null;
}

function dateOrNull(v) {
  if (v === null || v === undefined || v === '') return null;
  return v;
}

function omitNullish(obj) {
  const out = {};
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (v === null || v === undefined) continue;
    if (typeof v === 'string' && v === '') continue;
    out[k] = v;
  }
  return out;
}

function buildPrLinePayload(ln) {
  if (!ln || typeof ln !== 'object') return null;
  const norm = normalizePurchaseLineByItemType(ln);
  return omitNullish({
    line_no:       toNumber(pick(ln, 'line_no', 'lineNo', undefined)) || undefined,
    item_id:       trimOrNull(pick(ln, 'item_id',   'itemId',   null)),
    item_code:     trimOrNull(pick(ln, 'item_code', 'itemCode', null)),
    item_name:     trimOrNull(pick(ln, 'item_name', 'itemName', null)),
    item_type:     pickStringUpper(ln, 'item_type', 'itemType') || null,
    qty:           toNumber(pick(norm, 'qty', 'qty', 0)),
    uom:           pickStringUpper(ln, 'uom', 'uom') || null,
    width_mm:      pickStringUpper(norm, 'item_type', 'itemType') === ITEM_TYPE_GLASS
                     ? toNumber(norm.width_mm)
                     : null,
    height_mm:     pickStringUpper(norm, 'item_type', 'itemType') === ITEM_TYPE_GLASS
                     ? toNumber(norm.height_mm)
                     : null,
    length_value:  LENGTH_ITEM_TYPES.has(pickStringUpper(norm, 'item_type', 'itemType'))
                     ? toNumber(norm.length_value)
                     : null,
    estimated_rate: toNumber(pick(ln, 'estimated_rate', 'estimatedRate', 0)),
    remarks:        trimOrNull(pick(ln, 'remarks', 'remarks', null))
  });
}

function buildPoLinePayload(ln) {
  if (!ln || typeof ln !== 'object') return null;
  const norm = normalizePurchaseLineByItemType(ln);
  const itemType = pickStringUpper(norm, 'item_type', 'itemType');
  return omitNullish({
    line_no:          toNumber(pick(ln, 'line_no', 'lineNo', undefined)) || undefined,
    pr_line_id:       trimOrNull(pick(ln, 'pr_line_id',       'prLineId',       null)),
    item_id:          trimOrNull(pick(ln, 'item_id',          'itemId',          null)),
    item_code:        trimOrNull(pick(ln, 'item_code',        'itemCode',        null)),
    item_name:        trimOrNull(pick(ln, 'item_name',        'itemName',        null)),
    item_type:        itemType || null,
    qty:              toNumber(pick(norm, 'qty', 'qty', 0)),
    uom:              pickStringUpper(ln, 'uom', 'uom') || null,
    width_mm:         itemType === ITEM_TYPE_GLASS ? toNumber(norm.width_mm)  : null,
    height_mm:        itemType === ITEM_TYPE_GLASS ? toNumber(norm.height_mm) : null,
    length_value:     LENGTH_ITEM_TYPES.has(itemType)
                        ? toNumber(norm.length_value)
                        : null,
    rate:             toNumber(pick(ln, 'rate', 'rate', 0)),
    discount_percent: toNumber(pick(ln, 'discount_percent', 'discountPercent', 0)),
    gst_rate:         toNumber(pick(ln, 'gst_rate',         'gstRate',         0)),
    remarks:          trimOrNull(pick(ln, 'remarks', 'remarks', null))
  });
}

export function buildPurchaseRequestPayload(state) {
  const safe = state && typeof state === 'object' ? state : {};
  const lines = Array.isArray(safe.lines)
    ? safe.lines.map(buildPrLinePayload).filter((l) => l !== null)
    : [];
  return omitNullish({
    pr_date:          dateOrNull(safe.prDate          || safe.pr_date),
    required_by_date: dateOrNull(safe.requiredByDate  || safe.required_by_date),
    request_type:     trimOrNull(safe.requestType     || safe.request_type) || 'GENERAL',
    priority:         trimOrNull(safe.priority)       || 'NORMAL',
    status:           trimOrNull(safe.status)         || 'DRAFT',
    department:       trimOrNull(safe.department),
    requested_by:     trimOrNull(safe.requestedBy     || safe.requested_by),
    project_ref:      trimOrNull(safe.projectRef      || safe.project_ref),
    remarks:          trimOrNull(safe.remarks),
    lines
  });
}

export function buildPurchaseOrderPayload(state) {
  const safe = state && typeof state === 'object' ? state : {};
  const _bf = (safe.billFrom && typeof safe.billFrom === 'object')
    ? safe.billFrom
    : ((safe.bill_from && typeof safe.bill_from === 'object') ? safe.bill_from : {});
  const lines = Array.isArray(safe.lines)
    ? safe.lines.map(buildPoLinePayload).filter((l) => l !== null)
    : [];
  return omitNullish({
    po_date:                dateOrNull(safe.poDate                || safe.po_date),
    expected_delivery_date: dateOrNull(safe.expectedDeliveryDate  || safe.expected_delivery_date),
    status:                 trimOrNull(safe.status)               || 'DRAFT',

    // Legacy flat supplier_* columns are DERIVED from the Bill From snapshot
    // so every existing reader (GST split, GRN, Bill) keeps working.
    supplier_id:            trimOrNull(safe.supplierId            || safe.supplier_id),
    supplier_name:          trimOrNull(_bf.name)        || trimOrNull(safe.supplierName || safe.supplier_name),
    supplier_gstin:         trimOrNull(_bf.gstin)       || trimOrNull(safe.supplierGstin || safe.supplier_gstin),
    supplier_contact:       trimOrNull(_bf.mobile)      || trimOrNull(safe.supplierContact || safe.supplier_contact),
    supplier_address:       trimOrNull(_bf.addressLine1)|| trimOrNull(safe.supplierAddress || safe.supplier_address),
    supplier_state_code:    trimOrNull(_bf.stateCode)   || trimOrNull(safe.supplierStateCode || safe.supplier_state_code),
    company_state_code:     trimOrNull(safe.companyStateCode      || safe.company_state_code),
    bill_from:              buildShipToPayload(safe.billFrom || safe.bill_from),

    pr_id:                  trimOrNull(safe.sourcePrId            || safe.source_pr_id || safe.prId),
    pr_number_snapshot:     trimOrNull(safe.prNumberSnapshot      || safe.pr_number_snapshot),

    delivery_location:      trimOrNull(safe.deliveryLocation      || safe.delivery_location),
    payment_terms:          trimOrNull(safe.paymentTerms          || safe.payment_terms),
    delivery_terms:         trimOrNull(safe.deliveryTerms         || safe.delivery_terms),
    freight_terms:          trimOrNull(safe.freightTerms          || safe.freight_terms),
    remarks:                trimOrNull(safe.remarks),
    // Ad-hoc manual Other Charges — always sent (even empty) so clearing
    // the last charge persists. amount is recomputed server-side.
    other_charges:          buildOtherChargesPayload(safe.otherCharges || safe.other_charges),
    // Structured delivery address (Ship To). Always sent so clearing persists.
    ship_to:                buildShipToPayload(safe.shipTo || safe.ship_to),
    lines
  });
}

// Structured delivery address (Ship To) — same camelCase keys as the
// proforma ship_to snapshot.
const SHIP_TO_KEYS = [
  'name', 'addressLine1', 'addressLine2', 'city', 'pincode',
  'stateCode', 'state', 'mobile', 'email', 'gstin'
];
function normalizeShipTo(obj) {
  const safe = obj && typeof obj === 'object' && !Array.isArray(obj) ? obj : {};
  const out = {};
  for (const k of SHIP_TO_KEYS) {
    const snake = k.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`);
    out[k] = (safe[k] != null ? safe[k] : (safe[snake] != null ? safe[snake] : '')) || '';
  }
  return out;
}
function buildShipToPayload(obj) {
  const safe = obj && typeof obj === 'object' ? obj : {};
  const out = {};
  for (const k of SHIP_TO_KEYS) {
    const v = trimOrNull(safe[k]);
    if (v != null) out[k] = v;
  }
  return out;
}

// Supplier "Bill From" snapshot — same shape as Ship To. On read, fall back
// to the legacy flat supplier_* columns so POs saved before this feature
// still populate the structured block.
function normalizeBillFrom(api) {
  const safe = api && typeof api === 'object' ? api : {};
  const raw = pick(safe, 'bill_from', 'billFrom', null);
  const bf = normalizeShipTo(raw);
  const hasAny = SHIP_TO_KEYS.some((k) => String(bf[k] || '').trim());
  if (hasAny) return bf;
  // Legacy fallback from the flat columns.
  return Object.assign(bf, {
    name:         safe.supplierName     || safe.supplier_name       || '',
    addressLine1: safe.supplierAddress  || safe.supplier_address    || '',
    stateCode:    safe.supplierStateCode|| safe.supplier_state_code || '',
    mobile:       safe.supplierContact  || safe.supplier_contact    || '',
    gstin:        safe.supplierGstin    || safe.supplier_gstin      || ''
  });
}

// Map the editor's other-charge rows → the API shape. Drops blank rows
// (no charge_name). amount is intentionally omitted — the server recomputes.
function buildOtherChargesPayload(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((c) => {
      const safe = c && typeof c === 'object' ? c : {};
      const name = trimOrNull(safe.chargeName || safe.charge_name);
      if (!name) return null;
      return omitNullish({
        manual_charge_id: trimOrNull(safe.manualChargeId || safe.manual_charge_id),
        charge_name:      name,
        area:             toNumber(safe.area) || 0,
        rate:             toNumber(safe.rate) || 0
      });
    })
    .filter((c) => c !== null);
}


// ─────────────────────────────────────────────────────────────────
// Status labels + tag colours. Colours align with the AntD Tag
// palette guidance in docs/23-antd-component-standards.md so the
// PR/PO grids match the rest of the control-plane chrome.
// ─────────────────────────────────────────────────────────────────

const STATUS_LABELS = {
  DRAFT:              'Draft',
  REQUESTED:          'Requested',
  APPROVED:           'Approved',
  REJECTED:           'Rejected',
  CONVERTED_TO_PO:    'Converted to PO',
  CANCELLED:          'Cancelled',
  CLOSED:             'Closed',
  PLACED:             'Placed',
  PARTIALLY_RECEIVED: 'Partially Received'
};

const STATUS_COLORS = {
  DRAFT:              'default',
  REQUESTED:          'processing',
  APPROVED:           'success',
  REJECTED:           'error',
  CONVERTED_TO_PO:    'blue',
  CANCELLED:          'default',
  CLOSED:             'default',
  PLACED:             'processing',
  PARTIALLY_RECEIVED: 'gold'
};

export function statusLabel(status) {
  if (!status) return '';
  const key = String(status).trim().toUpperCase();
  return STATUS_LABELS[key] || key;
}

export function statusColor(status) {
  if (!status) return 'default';
  const key = String(status).trim().toUpperCase();
  return STATUS_COLORS[key] || 'default';
}


// ─────────────────────────────────────────────────────────────────
// Per-status capability gates — used by the page to enable/disable
// toolbar buttons. The backend remains the authority; these guards
// only hide CTAs the operator can't possibly succeed with.
// ─────────────────────────────────────────────────────────────────

function prStatus(pr) {
  if (!pr || typeof pr !== 'object') return null;
  const s = String(pr.status || '').trim().toUpperCase();
  return s || null;
}

function poStatus(po) {
  if (!po || typeof po !== 'object') return null;
  const s = String(po.status || '').trim().toUpperCase();
  return s || null;
}

export function canEditPurchaseRequest(pr) {
  const s = prStatus(pr);
  return s === 'DRAFT' || s === 'REQUESTED';
}

export function canSubmitPurchaseRequest(pr) {
  return prStatus(pr) === 'DRAFT';
}

export function canApprovePurchaseRequest(pr) {
  return prStatus(pr) === 'REQUESTED';
}

export function canRejectPurchaseRequest(pr) {
  return prStatus(pr) === 'REQUESTED';
}

export function canCancelPurchaseRequest(pr) {
  const s = prStatus(pr);
  return s === 'DRAFT' || s === 'REQUESTED' || s === 'APPROVED';
}

export function canConvertPurchaseRequest(pr) {
  return prStatus(pr) === 'APPROVED';
}

export function canDeletePurchaseRequest(pr) {
  return prStatus(pr) === 'DRAFT';
}

export function canEditPurchaseOrder(po) {
  return poStatus(po) === 'DRAFT';
}

export function canPlacePurchaseOrder(po) {
  return poStatus(po) === 'DRAFT';
}

export function canCancelPurchaseOrder(po) {
  const s = poStatus(po);
  return s === 'DRAFT' || s === 'PLACED' || s === 'PARTIALLY_RECEIVED';
}

export function canClosePurchaseOrder(po) {
  const s = poStatus(po);
  return s === 'PLACED' || s === 'PARTIALLY_RECEIVED';
}

export function canDeletePurchaseOrder(po) {
  return poStatus(po) === 'DRAFT';
}


// ─────────────────────────────────────────────────────────────────
// Last-mile validation — runs locally before opening the network
// call. Returns the same { ok, errors: [{ field, message,
// clientRowId? }] } shape glassCategoryHelpers uses so the page can
// share the AntD Form.ErrorList renderer.
//
// Mirrors the Joi shape rules in purchase.validation.js — the
// backend remains the authority and will reject anything we miss.
// ─────────────────────────────────────────────────────────────────

function pushErr(errors, field, message, clientRowId) {
  const e = { field, message };
  if (clientRowId) e.clientRowId = clientRowId;
  errors.push(e);
}

function isISODateLike(v) {
  if (v === null || v === undefined || v === '') return false;
  if (v instanceof Date) return !Number.isNaN(v.getTime());
  const d = new Date(v);
  return !Number.isNaN(d.getTime());
}

function compareDates(a, b) {
  // Returns sign(a - b) in ms, or 0 if either side is unparsable.
  if (!isISODateLike(a) || !isISODateLike(b)) return 0;
  return new Date(a).getTime() - new Date(b).getTime();
}

function validatePrLine(ln, errors) {
  if (!ln || typeof ln !== 'object') {
    pushErr(errors, 'lines', 'Invalid line.');
    return;
  }
  const cid = ln._clientRowId;
  const itemType = pickStringUpper(ln, 'item_type', 'itemType');
  const uom      = pickStringUpper(ln, 'uom', 'uom');
  const qty      = toNumber(pick(ln, 'qty', 'qty', 0));
  const name     = trimOrNull(pick(ln, 'item_name', 'itemName', null));

  if (!name) {
    pushErr(errors, 'item_name', 'Stock item is required.', cid);
  }
  if (!itemType) {
    pushErr(errors, 'item_type', 'Item type is required.', cid);
  } else if (!VALID_ITEM_TYPES.has(itemType)) {
    pushErr(errors, 'item_type',
      `Item type must be one of: ${[...VALID_ITEM_TYPES].join(', ')}.`, cid);
  }
  if (qty <= 0) {
    pushErr(errors, 'qty', 'Quantity must be greater than 0.', cid);
  }
  if (!uom) {
    pushErr(errors, 'uom', 'UOM is required.', cid);
  }

  if (itemType === ITEM_TYPE_GLASS) {
    const w = toNumber(pick(ln, 'width_mm',  'widthMm',  0));
    const h = toNumber(pick(ln, 'height_mm', 'heightMm', 0));
    if (w <= 0) pushErr(errors, 'width_mm',  'Width is required for glass lines.',  cid);
    if (h <= 0) pushErr(errors, 'height_mm', 'Height is required for glass lines.', cid);
  } else if (LENGTH_ITEM_TYPES.has(itemType) && LENGTH_UOMS_SET.has(uom)) {
    const len = toNumber(pick(ln, 'length_value', 'lengthValue', 0));
    if (len <= 0) {
      pushErr(errors, 'length_value', 'Length is required for profile lines.', cid);
    }
  }

  const estRate = toNumber(pick(ln, 'estimated_rate', 'estimatedRate', 0));
  if (estRate < 0) {
    pushErr(errors, 'estimated_rate', 'Estimated rate cannot be negative.', cid);
  }
}

function validatePoLine(ln, errors) {
  if (!ln || typeof ln !== 'object') {
    pushErr(errors, 'lines', 'Invalid line.');
    return;
  }
  const cid = ln._clientRowId;
  const itemType = pickStringUpper(ln, 'item_type', 'itemType');
  const uom      = pickStringUpper(ln, 'uom', 'uom');
  const qty      = toNumber(pick(ln, 'qty', 'qty', 0));
  const name     = trimOrNull(pick(ln, 'item_name', 'itemName', null));

  if (!name) {
    pushErr(errors, 'item_name', 'Stock item is required.', cid);
  }
  if (!itemType) {
    pushErr(errors, 'item_type', 'Item type is required.', cid);
  } else if (!VALID_ITEM_TYPES.has(itemType)) {
    pushErr(errors, 'item_type',
      `Item type must be one of: ${[...VALID_ITEM_TYPES].join(', ')}.`, cid);
  }
  if (qty <= 0) {
    pushErr(errors, 'qty', 'Quantity must be greater than 0.', cid);
  }
  if (!uom) {
    pushErr(errors, 'uom', 'UOM is required.', cid);
  }

  if (itemType === ITEM_TYPE_GLASS) {
    const w = toNumber(pick(ln, 'width_mm',  'widthMm',  0));
    const h = toNumber(pick(ln, 'height_mm', 'heightMm', 0));
    if (w <= 0) pushErr(errors, 'width_mm',  'Width is required for glass lines.',  cid);
    if (h <= 0) pushErr(errors, 'height_mm', 'Height is required for glass lines.', cid);
  } else if (LENGTH_ITEM_TYPES.has(itemType) && LENGTH_UOMS_SET.has(uom)) {
    const len = toNumber(pick(ln, 'length_value', 'lengthValue', 0));
    if (len <= 0) {
      pushErr(errors, 'length_value', 'Length is required for profile lines.', cid);
    }
  }

  const rate = toNumber(pick(ln, 'rate', 'rate', 0));
  if (rate < 0) {
    pushErr(errors, 'rate', 'Rate cannot be negative.', cid);
  }
  const disc = toNumber(pick(ln, 'discount_percent', 'discountPercent', 0));
  if (disc < 0 || disc > 100) {
    pushErr(errors, 'discount_percent',
      'Discount percent must be between 0 and 100.', cid);
  }
  const gst = toNumber(pick(ln, 'gst_rate', 'gstRate', 0));
  if (gst < 0 || gst > 28) {
    pushErr(errors, 'gst_rate', 'GST rate must be between 0 and 28.', cid);
  }
}

export function validatePurchaseRequest(state) {
  const errors = [];
  if (!state || typeof state !== 'object') {
    pushErr(errors, 'state', 'Purchase request is required.');
    return { ok: false, errors };
  }

  const prDate         = state.prDate         || state.pr_date;
  const requiredBy     = state.requiredByDate || state.required_by_date;
  const status         = state.status;
  const requestType    = state.requestType    || state.request_type;
  const priority       = state.priority;

  if (status && !VALID_PR_STATUSES.has(String(status).toUpperCase())) {
    pushErr(errors, 'status',
      `Status must be one of: ${[...VALID_PR_STATUSES].join(', ')}.`);
  }
  if (requestType) {
    const rt = String(requestType).toUpperCase();
    const allowed = new Set(PR_REQUEST_TYPE_OPTIONS.map((o) => o.value));
    if (!allowed.has(rt)) {
      pushErr(errors, 'request_type',
        `Request type must be one of: ${[...allowed].join(', ')}.`);
    }
  }
  if (priority) {
    const allowed = new Set(PR_PRIORITY_OPTIONS.map((o) => o.value));
    if (!allowed.has(String(priority).toUpperCase())) {
      pushErr(errors, 'priority',
        `Priority must be one of: ${[...allowed].join(', ')}.`);
    }
  }
  if (prDate && requiredBy && compareDates(requiredBy, prDate) < 0) {
    pushErr(errors, 'required_by_date',
      'Required by date cannot be before PR date.');
  }

  const lines = Array.isArray(state.lines) ? state.lines : [];
  if (lines.length === 0) {
    pushErr(errors, 'lines', 'Add at least one line to the purchase request.');
  } else {
    for (const ln of lines) validatePrLine(ln, errors);
  }

  return { ok: errors.length === 0, errors };
}

export function validatePurchaseOrder(state) {
  const errors = [];
  if (!state || typeof state !== 'object') {
    pushErr(errors, 'state', 'Purchase order is required.');
    return { ok: false, errors };
  }

  const poDate         = state.poDate                || state.po_date;
  const expectedDelivery =
    state.expectedDeliveryDate || state.expected_delivery_date;
  const status         = state.status;
  const supplierName   = trimOrNull(state.supplierName || state.supplier_name);
  const supplierState  = pickStringUpper(state, 'supplier_state_code', 'supplierStateCode');
  const companyState   = pickStringUpper(state, 'company_state_code',  'companyStateCode');

  if (!poDate) {
    pushErr(errors, 'po_date', 'PO date is required.');
  }
  if (!supplierName) {
    pushErr(errors, 'supplier_name', 'Supplier name is required.');
  }
  if (status && !VALID_PO_STATUSES.has(String(status).toUpperCase())) {
    pushErr(errors, 'status',
      `Status must be one of: ${[...VALID_PO_STATUSES].join(', ')}.`);
  }
  if (poDate && expectedDelivery && compareDates(expectedDelivery, poDate) < 0) {
    pushErr(errors, 'expected_delivery_date',
      'Expected delivery date cannot be before PO date.');
  }
  if (supplierState && supplierState.length !== 2) {
    pushErr(errors, 'supplier_state_code',
      'State code must be exactly 2 characters.');
  }
  if (companyState && companyState.length !== 2) {
    pushErr(errors, 'company_state_code',
      'State code must be exactly 2 characters.');
  }

  const lines = Array.isArray(state.lines) ? state.lines : [];
  if (lines.length === 0) {
    pushErr(errors, 'lines', 'Add at least one line to the purchase order.');
  } else {
    for (const ln of lines) validatePoLine(ln, errors);
  }

  return { ok: errors.length === 0, errors };
}
